import { t } from 'i18next';
import { Controller } from 'react-hook-form';
import { ILead } from '@clickersphere/core/entities/ILead';
import { LEAD_SOURCE_VALUES } from '@clickersphere/core/enums/LeadSourceEnum';
import BadgeCombobox from '../../Badge/BadgeCombobox';
import { Button } from '../../Button';
import { Input } from '../../Input';
import Select from '../../Select';
import { leadSourceLabelKeys } from '../../Lead/leadEnumLabels';
import ReferrerCombobox from '../../Referrer/ReferrerCombobox';
import { useRegisterLeadFormController } from './useRegisterLeadFormController';

interface ILeadFormProps {
  lead?: ILead;
}

export default function LeadForm({ lead }: ILeadFormProps) {
  const {
    isFetching,
    errors,
    control,
    handleSubmit,
    register,
    handleFormReset,
    selectedBadgeIds,
    handleBadgeIdsChange,
    setSelectedReferrerName,
  } = useRegisterLeadFormController({ lead });

  return (
    <form
      className="my-4 flex w-full flex-col items-center gap-4"
      onSubmit={handleSubmit}
      noValidate
    >
      <Input
        type="text"
        placeholder={t('register.fields.name')}
        {...register('name')}
        error={errors.name?.message}
        autoComplete="name"
        disabled={isFetching}
      />
      <Input
        type="email"
        placeholder={t('register.fields.email')}
        {...register('email')}
        error={errors.email?.message}
        autoComplete="new-email"
        disabled={isFetching}
      />
      <Input
        type="tel"
        placeholder={t('register.fields.phone')}
        {...register('phone')}
        error={errors.phone?.message}
        autoComplete="tel"
        disabled={isFetching}
      />
      <Input
        type="text"
        placeholder={t('register.fields.instagram')}
        {...register('instagram')}
        error={errors.instagram?.message}
        disabled={isFetching}
      />
      <Input
        type="text"
        placeholder={t('register.fields.notes')}
        {...register('notes')}
        error={errors.notes?.message}
        disabled={isFetching}
      />

      <Controller
        control={control}
        name="source"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            placeholder={t('register.fields.source')}
            options={LEAD_SOURCE_VALUES.map((source) => ({
              value: source,
              text: t(leadSourceLabelKeys[source]),
            }))}
            emptyOption
            emptyOptionLabel={t('register.fields.sourceNone')}
            value={(value as string) ?? ''}
            onChange={onChange}
            error={error?.message}
            disabled={isFetching}
          />
        )}
      />

      <div className="flex w-full flex-col gap-1">
        <label className="text-text-tertiary text-xs">
          {t('register.fields.referrer')}
        </label>
        <Controller
          control={control}
          name="referrerId"
          render={({ field: { value, onChange } }) => (
            <ReferrerCombobox
              value={value ?? null}
              onChange={(referrerId, referrerName) => {
                onChange(referrerId ?? '');
                setSelectedReferrerName(referrerName);
              }}
              disabled={isFetching}
            />
          )}
        />
      </div>

      <div className="flex w-full flex-col gap-1">
        <label className="text-text-tertiary text-xs" htmlFor="badge-combobox">
          {t('register.fields.badges')}
        </label>
        <BadgeCombobox
          selectedBadgeIds={selectedBadgeIds}
          onChange={handleBadgeIdsChange}
          disabled={isFetching}
        />
      </div>

      <section className="mt-4 flex w-full gap-4">
        <Button
          className="w-50"
          labelPath="register.clearFields"
          disabled={isFetching}
          color="ghost"
          onClick={() => handleFormReset()}
        />
        <Button
          labelPath="register.submit"
          type="submit"
          disabled={isFetching}
          isLoading={isFetching}
        />
      </section>
    </form>
  );
}
